import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { toast } from "sonner";
import { ArrowLeft, Pencil, ShieldAlert, ShieldCheck } from "lucide-react";
import type { components } from "@dolfin/api-public";
import { useDolfinClient } from "@/components/DolfinProvider";
import { EditSupplierDialog } from "@/components/EditSupplierDialog";
import { ConfirmBankDetailsDialog } from "@/components/ConfirmBankDetailsDialog";
import { usePermissions } from "@/lib/permissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import { AddressDisplay, hasAddress } from "@/components/AddressFields";
import { BankDetailsDisplay, hasBankDetails } from "@/components/BankDetailsFields";
import { formatCurrency, formatDate } from "@/lib/format";
import { BillsTable } from "@/components/BillsTable";
import { PurchaseOrdersTable } from "@/components/PurchaseOrdersTable";
import { DeliveryNotesTable } from "@/components/DeliveryNotesTable";
import { SupplierCreditTab } from "@/components/SupplierCreditTab";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { ErrorState, Eyebrow, LoadingState } from "@/components/PageUi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { apiErrorMessage } from "@/lib/api-error";
import { accountLabel, type LedgerAccount } from "@/components/AccountingSettingsSections";
import { isExpense, sortBy } from "@/lib/accounting-utils";

type SupplierResponse = components["schemas"]["SupplierResponse"];
type SupplierCreditBalanceResponse = components["schemas"]["SupplierCreditBalanceResponse"];

export interface SupplierPageProps {
  supplierId: string;
  /** Called when the user clicks the back affordance. Falls back to history.back() if neither this nor LinkComponent is provided. */
  onBack?: () => void;
  /** Renders the back link as a routed anchor (e.g. next/link, react-router Link). Receives `href="/suppliers"`. */
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  /** Path passed to LinkComponent if provided. Defaults to "/suppliers". */
  backHref?: string;
  onBillClick?: (billId: string) => void;
  onMatchClick?: (billId: string) => void;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
  /** The client holds ap.accounting and a platform is connected: shows the supplier's default account picker. */
  accountingConnected?: boolean;
}

const ORIGIN_LABELS: Record<string, string> = {
  Manual: "Added by hand",
  BillExtraction: "Read off a bill",
  EInvoice: "Received over the e-invoicing network",
  Unknown: "Not recorded",
};

function originLabel(origin?: string) {
  return origin ? (ORIGIN_LABELS[origin] ?? origin) : undefined;
}

export function SupplierPage({
  supplierId,
  onBack,
  LinkComponent,
  backHref = "/suppliers",
  onBillClick,
  onMatchClick,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
  onCreditNoteClick,
  accountingConnected,
}: SupplierPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const [supplier, setSupplier] = useState<SupplierResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmingBank, setConfirmingBank] = useState(false);
  // Credit this supplier owes us, per currency. Shown in the header because it changes what to do with
  // their next bill: net it off rather than pay in full.
  const [credit, setCredit] = useState<SupplierCreditBalanceResponse[]>([]);

  async function fetchSupplier() {
    if (!supplierId) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await api.GET("/v1/suppliers/{id}", {
        params: { path: { id: supplierId } },
      });

      if (apiError || !data) {
        console.error("Failed to load supplier:", apiError);
        setError("Failed to load supplier");
        return;
      }

      setSupplier(data);
    } catch (e) {
      console.error("Failed to load supplier:", e);
      setError("Failed to load supplier");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSupplier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  useEffect(() => {
    if (!supplierId) return;
    let cancelled = false;
    api
      .GET("/v1/suppliers/{supplierId}/credit", { params: { path: { supplierId } } })
      .then(({ data }) => {
        if (!cancelled) setCredit((data ?? []).filter((b) => Number(b.available ?? 0) > 0));
      });
    return () => {
      cancelled = true;
    };
  }, [api, supplierId]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Loading supplier..." />
      </PageContainer>
    );
  }

  if (error || !supplier) {
    return (
      <PageContainer>
        <BackAffordance onBack={onBack} LinkComponent={LinkComponent} backHref={backHref} />
        <ErrorState message={error || "That supplier could not be found."} onRetry={fetchSupplier} />
      </PageContainer>
    );
  }

  const addr = supplier.address ?? undefined;
  const location = [addr?.city, addr?.country].filter(Boolean).join(", ");

  return (
    <PageContainer>
      <BackAffordance
        onBack={onBack}
        LinkComponent={LinkComponent}
        backHref={backHref}
      />

      <PageHeader
        eyebrow="Supplier"
        title={supplier.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {[supplier.email, location, supplier.paymentTermsDefault ? `Terms ${supplier.paymentTermsDefault}` : null]
              .filter(Boolean)
              .map((part, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 ? <span aria-hidden>·</span> : null}
                  {part}
                </span>
              ))}
            {credit.map((b) => (
              <span
                key={b.currency}
                className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 tabular-nums dark:text-emerald-400"
              >
                {formatCurrency(b.available, b.currency)} credit available
              </span>
            ))}
          </span>
        }
        actions={
          <PermissionGuard
            allowed={can("manageSuppliers")}
            reason="Only owners and admins can edit suppliers"
          >
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="size-4 mr-1" />
              Edit
            </Button>
          </PermissionGuard>
        }
      />

      <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
        <DetailCard title="Contact">
          <DetailRow label="Email" value={supplier.email} />
          <DetailRow label="Phone" value={supplier.phone} />
          <DetailRow label="Website" value={supplier.website} />
          <DetailRow label="Tax number" value={supplier.taxNumber} />
          <DetailRow label="Registration number" value={supplier.registrationNumber} />
          <DetailRow label="Payment terms" value={supplier.paymentTermsDefault} />
          {/* EInvoice or BillExtraction means inferred from a document, not confirmed here. Unknown predates the field; not shown as Manual. */}
          <DetailRow label="Created from" value={originLabel(supplier.origin)} />
        </DetailCard>

        {hasAddress(addr) && (
          <DetailCard title="Address">
            <AddressDisplay address={addr} />
          </DetailCard>
        )}

        {hasBankDetails(supplier.bankDetails) && (
          <DetailCard title="Bank details">
            <div className="flex items-center gap-2">
              {supplier.bankDetailsVerified ? (
                <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                  <ShieldCheck className="size-3" />
                  Confirmed for pay-by-bank
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  <ShieldAlert className="size-3" />
                  Not confirmed
                </Badge>
              )}
            </div>
            <BankDetailsDisplay bankDetails={supplier.bankDetails} />
            {supplier.bankDetailsVerified ? (
              supplier.bankDetailsVerifiedAt && (
                <p className="text-xs text-muted-foreground">
                  Confirmed {formatDate(supplier.bankDetailsVerifiedAt, "long")}. Editing the
                  account details clears this confirmation.
                </p>
              )
            ) : (
              <PermissionGuard
                allowed={can("manageSuppliers")}
                reason="Only owners and admins can confirm supplier bank details"
              >
                <Button variant="outline" size="sm" onClick={() => setConfirmingBank(true)}>
                  <ShieldCheck className="size-4 mr-1" />
                  Confirm for pay-by-bank
                </Button>
              </PermissionGuard>
            )}
          </DetailCard>
        )}

        {accountingConnected ? (
          <SupplierDefaultAccount
            supplier={supplier}
            canEdit={can("manageSuppliers")}
            onSaved={setSupplier}
          />
        ) : null}

        <DetailCard title="Record">
          <DetailRow label="Supplier ID" value={supplier.id} mono />
          <DetailRow label="Created" value={formatDate(supplier.createdAt, "long")} />
          <DetailRow label="Last updated" value={formatDate(supplier.updatedAt, "long")} />
        </DetailCard>
      </div>

      <div className="mt-8">
        <Tabs defaultValue="bills">
          <TabsList>
            <TabsTrigger value="bills">Bills</TabsTrigger>
            <TabsTrigger value="purchase-orders">Purchase orders</TabsTrigger>
            <TabsTrigger value="delivery-notes">Delivery notes</TabsTrigger>
            <TabsTrigger value="credit">Credit</TabsTrigger>
          </TabsList>
          <TabsContent value="bills">
            <BillsTable
              filters={{ supplierId }}
              hideSupplierColumn
              onBillClick={onBillClick ?? (() => {})}
              onMatchClick={onMatchClick}
            />
          </TabsContent>
          <TabsContent value="purchase-orders">
            <PurchaseOrdersTable supplierId={supplierId} onSelect={onPurchaseOrderClick} />
          </TabsContent>
          <TabsContent value="delivery-notes">
            <DeliveryNotesTable supplierId={supplierId} onSelect={onDeliveryNoteClick} />
          </TabsContent>
          <TabsContent value="credit">
            <SupplierCreditTab
              supplierId={supplierId}
              onBillClick={onBillClick}
              onCreditNoteClick={onCreditNoteClick}
            />
          </TabsContent>
        </Tabs>
      </div>

      <EditSupplierDialog
        supplier={supplier}
        open={editing}
        onOpenChange={setEditing}
        onUpdated={fetchSupplier}
      />

      <ConfirmBankDetailsDialog
        supplier={supplier}
        open={confirmingBank}
        onOpenChange={setConfirmingBank}
        onConfirmed={fetchSupplier}
      />
    </PageContainer>
  );
}

function BackAffordance({
  onBack,
  LinkComponent,
  backHref,
}: {
  onBack?: () => void;
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  backHref: string;
}) {
  const className =
    "mb-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground";
  const content = (
    <>
      <ArrowLeft className="size-3.5" />
      Back to suppliers
    </>
  );

  if (LinkComponent) {
    return (
      <LinkComponent href={backHref} className={className}>
        {content}
      </LinkComponent>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={() => (onBack ? onBack() : window.history.back())}
    >
      {content}
    </button>
  );
}

// Defaults to the supplier's purchases account in the platform on first push, unless chosen first.
function SupplierDefaultAccount({
  supplier,
  canEdit,
  onSaved,
}: {
  supplier: SupplierResponse;
  canEdit: boolean;
  onSaved: (supplier: SupplierResponse) => void;
}) {
  const api = useDolfinClient();
  const [accounts, setAccounts] = useState<LedgerAccount[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.GET("/v1/accounting/ledger-accounts", {}).then(({ data }) => {
      if (!cancelled) setAccounts(data ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const sorted = useMemo(() => sortBy(accounts ?? [], isExpense), [accounts]);

  async function save(defaultLedgerAccountId: string) {
    setSaving(true);
    const { data, error } = await api.PATCH("/v1/suppliers/{id}", {
      params: { path: { id: supplier.id! } },
      body: { defaultLedgerAccountId },
    });
    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't save the supplier's default account."));
    } else {
      onSaved(data);
      toast.success("Default account saved.");
    }
    setSaving(false);
  }

  return (
    <DetailCard title="Accounting">
      <div className="flex items-center gap-2">
        <Select
          value={supplier.defaultLedgerAccountId ?? undefined}
          onValueChange={save}
          disabled={saving || !canEdit || accounts === null || sorted.length === 0}
        >
          <SelectTrigger className="w-full" aria-label="Default account">
            <SelectValue
              placeholder={
                accounts === null
                  ? "Loading accounts..."
                  : sorted.length === 0
                    ? "No accounts synced"
                    : "Organisation default expense account"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {supplier.defaultLedgerAccountId && accounts && !sorted.some((a) => a.id === supplier.defaultLedgerAccountId) ? (
              <SelectItem value={supplier.defaultLedgerAccountId}>An archived account</SelectItem>
            ) : null}
            {sorted.map((a) => (
              <SelectItem key={a.id} value={a.id!}>
                {accountLabel(a)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {saving ? <Spinner className="size-4 shrink-0 text-muted-foreground" /> : null}
      </div>
      <p className="text-xs text-muted-foreground">
        Where this supplier's bill lines post in your books, unless a line names its own account.
        {canEdit ? "" : " Only an Owner or Admin can change it."}
      </p>
    </DetailCard>
  );
}

function DetailCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <Eyebrow className="mb-3">{title}</Eyebrow>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`min-w-0 break-words text-right font-medium ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </span>
    </div>
  );
}
